import { PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_PAGE, GET_ALL_RECORDS } from "../domain/constants";
import {
    GetInterestingWorksParams,
    GetWorksParams,
    WorkAutocompleteDTO,
    WorkCollectionMeta,
    WorkCreationForm,
    WorkDTO,
} from "../domain/models/dto/WorkDTO";
import { WorksUsersDTO } from "../domain/models/dto/WorksUsersDTO";
import { WorksState } from "../domain/models/Work";
import addressMapperFactory from "../mappers/AddressMapper";
import workMapperFactory from "../mappers/WorkMapper";
import workSearchSortMapperFactory from "../mappers/WorkSearchSortMapper";
import { CollectionResponse, SimpleResponse } from "../types/ApiResults";
import { ApiServiceImpl, createAsyncThunkFactory } from "./ApiServiceImpl";

const saveWorkRequests = new Map<string, Promise<WorksUsersDTO>>();

export class WorkServiceImpl extends ApiServiceImpl {
    private constructor() {
        super();
    }

    static getWorkByIdThunk = createAsyncThunkFactory<WorkDTO, { id: string }>(
        "WorkServiceImpl.getWorkById",
        async (args) => {
            return this.get(`/api/v1/work/${args.id}`);
        }
    );

    static getWorksThunk = createAsyncThunkFactory<
        CollectionResponse<WorkDTO, WorkCollectionMeta>,
        GetWorksParams
    >("WorkServiceImpl.getWorks", async (args) => {
        const searchMapper = workSearchSortMapperFactory();
        const addressMapper = addressMapperFactory();

        return this.getCollection("/api/v1/work", {
            page: args.page,
            limit: args.limit,
            getCurrentUserApplications: args.getCurrentUserApplications,
            currentUserApplicationStatus: args.currentUserApplicationStatus,
            workStatus: args.workStatus,
            getCurrentUserWorks: args.getCurrentUserWorks,
            getSavedWorks: args.getSavedWorks,
            clientId: args.clientId,
            coords: args.coords && JSON.stringify(args.coords),
            ...searchMapper.toDTO(args.form),
            ...addressMapper.toDTO(args.form?.address),
        });
    });

    static getInterestingWorksThunk = createAsyncThunkFactory<
        CollectionResponse<WorkDTO, WorkCollectionMeta>,
        GetInterestingWorksParams
    >("WorkServiceImpl.getInterestingWorks", async (args) => {
        return this.getCollection("/api/v1/work", {
            ...args,
            showOnlyActive: true,
            getUnrelatedWorks: true,
            coords: args.coords && JSON.stringify(args.coords),
        });
    });

    static autocompleteNamesThunk = createAsyncThunkFactory<
        WorkAutocompleteDTO,
        { title: string }
    >("WorkServiceImpl.autocompleteNames", async (args) => {
        return this.get(
            `/api/v1/work/autocomplete/?page=${GET_ALL_RECORDS}&limit=${DEFAULT_PAGE}&title=${args.title}`
        );
    });

    static createWorkThunk = createAsyncThunkFactory<WorkDTO, WorkCreationForm>(
        "WorkServiceImpl.createWork",
        async (args) => {
            return this.post("/api/v1/work", workMapperFactory().toDTO(args));
        }
    );

    static deleteWorkByIdThunk = createAsyncThunkFactory<
        SimpleResponse,
        { id: string }
    >("WorkServiceImpl.deleteWorkById", async (args) => {
        return this.delete(`/api/v1/work/${args.id}`);
    });

    static completeWorkThunk = createAsyncThunkFactory<
        SimpleResponse,
        { id: string }
    >("WorkServiceImpl.completeWork", async (args) => {
        return this.patch(`/api/v1/work/${args.id}/complete`, {});
    });

    static saveWorkByIdThunk = createAsyncThunkFactory<
        WorksUsersDTO,
        { id: string }
    >("WorkServiceImpl.saveWorkById", async (args) => {
        const pendingRequest = saveWorkRequests.get(args.id);
        if (pendingRequest) return pendingRequest;

        const request = (async () => {
            const response = await this.patch<unknown>(
                `/api/v1/work/${args.id}/save`,
                {}
            );

            if (!isSaveWorkResponse(response, args.id)) {
                throw new Error("Invalid save work response");
            }

            return response;
        })();
        saveWorkRequests.set(args.id, request);

        try {
            return await request;
        } finally {
            saveWorkRequests.delete(args.id);
        }
    });
}

const isSaveWorkResponse = (
    response: unknown,
    requestedWorkId: string
): response is WorksUsersDTO => {
    if (!response || typeof response !== "object") return false;

    const workUser = response as Partial<WorksUsersDTO>;
    return (
        typeof workUser.id === "string" &&
        workUser.workId === requestedWorkId &&
        typeof workUser.userId === "string" &&
        typeof workUser.saved === "boolean" &&
        typeof workUser.createdAt === "string" &&
        typeof workUser.updatedAt === "string"
    );
};

export const saveWorkHandler = (
    state: WorksState,
    action: PayloadAction<WorksUsersDTO>,
    removeUnsaved = false
) => {
    if (removeUnsaved && !action.payload.saved) {
        const previousLength = state.rows.length;
        state.rows = state.rows.filter(
            (work) => work.id !== action.payload.workId
        );
        if (state.rows.length !== previousLength) {
            state.totalRows = Math.max(0, state.totalRows - 1);
        }
        return;
    }

    state.rows.forEach((work) => {
        if (work.id === action.payload.workId) {
            work.workUser = action.payload;
        }
    });
};
